/**
 * @description
 */

import umuWebview from '@umu/umu-webview';
import { $document } from '@umu/umu-dom';
import { getUniqueId, EventEmitterWrapper } from '@umu/umu-util';

import './index.scss';

const { isIos } = umuWebview.UA_INFO;
let manager: PlayerManager | null = null;

export type TMediaType = 'video' | 'audio';

export interface IMediaOptions {
    isPrimary?: boolean; // 是否页面中的主播放器，与其它效果不同的时，可以被恢复播放
    autoplay?: boolean;
    controls?: boolean;
    loop?: boolean;
    file: string; // 播放文件路径
    volume?: number; // 音量改成100分制
    lastPlayedTime?: number;
    fileId?: string;
}

// http://www.w3school.com.cn/html5/html5_ref_audio_video_dom.asp
export enum EMediaEvent {
    'playing' = 'playing',
    'canplay' = 'canplay',
    'waiting' = 'waiting',
    'progress' = 'progress',
    'stalled' = 'stalled',
    'loadstart' = 'loadstart',
    'timeupdate' = 'timeupdate',
    'pause' = 'pause',
    'ended' = 'ended',
    'error' = 'error',
    'touchstart' = 'touchstart',
}

export type TMediaEvent =
    | 'playing'
    | 'canplay'
    | 'waiting'
    | 'progress'
    | 'stalled'
    | 'loadstart'
    | 'timeupdate'
    | 'pause'
    | 'ended'
    | 'error';

interface IMediaConfig {
    uniqueId: string;
    type: TMediaType;
    options: IMediaOptions;
    isPrimary: boolean;
    isLoaded: boolean;
    isPlaying: boolean;
    lastPlayedTime: number;
}
const MediaList: { [key: string]: IMediaConfig } = {};

class PlayerManager extends EventEmitterWrapper {
    public static getInstance() {
        if (!manager) {
            manager = new PlayerManager();
        }
        return manager;
    }

    private playingUniqueId: string = '';
    private audioPlayer: HTMLAudioElement | null = null;
    private videoPlayer: HTMLVideoElement | null = null;

    constructor() {
        super();
    }

    public getPlayer(type: TMediaType) {
        if (type === 'video') {
            if (!this.videoPlayer) {
                this.videoPlayer = $document.createElement('video');
                this.initPlayer(this.videoPlayer, type);
                this.bindEvent(this.videoPlayer);
            }
            return this.videoPlayer;
        } else if (type === 'audio') {
            if (!this.audioPlayer) {
                this.audioPlayer = $document.createElement('audio');
                this.audioPlayer.className = 'audio-player-el';
                this.initPlayer(this.audioPlayer, type);
                this.bindEvent(this.audioPlayer);
            }
            return this.audioPlayer;
        } else {
            console.error('播放器类型错误，无法获取实例');
            return null;
        }
    }

    public add(type: TMediaType, options: IMediaOptions) {
        const uniqueId = getUniqueId(type + '-');
        MediaList[uniqueId] = {
            uniqueId,
            type,
            options,
            isPrimary: !!options.isPrimary,
            isLoaded: false,
            isPlaying: false,
            lastPlayedTime: options.lastPlayedTime || 0,
        };
        return uniqueId;
    }

    public getCurrentMediaConfig() {
        const uniqueId = this.playingUniqueId;
        const config = MediaList[uniqueId];
        return config;
    }

    public initPlayerOptions(config: IMediaConfig) {
        const player = this.getPlayer(config.type);
        if (!player) {
            return;
        }

        const options = config.options;

        player.autoplay = !!options.autoplay;
        player.controls = !!options.controls;
        player.loop = !!options.loop;
        player.volume = (options.volume || 0) / 100;

        // 音频重复设置没问题，视频重复设置会闪一次，因此控制一下
        // HLS 的模式要排除掉
        if (!options.fileId && player.src !== options.file) {
            player.src = options.file;
        }
    }

    public load(uniqueId: string) {
        const config = MediaList[uniqueId];

        // 只有页面中的主播放器才可预加载
        if (!config.isPrimary) {
            return;
        }

        if (config.options.fileId) {
            this.playingUniqueId = uniqueId;
            return;
        }

        const player = this.getPlayer(config.type);

        if (player) {
            if (!config.isLoaded) {
                this.initPlayerOptions(config);
                // player.src = config.options.file;
                config.isLoaded = true;
            }
        }
        this.playingUniqueId = uniqueId;
        return player;
    }

    public play(uniqueId: string) {
        this.stopOtherPlayer(uniqueId);
        // 由于 setTimoeut 导致没有用户操作权限，有时无法启动播放，暂时去掉
        // setTimeout(() => {
        this.playNext(uniqueId);
        // }, 100);
    }

    public playNext(uniqueId: string) {
        const oldConfig = this.getCurrentMediaConfig();

        this.playingUniqueId = uniqueId;
        const config = this.getCurrentMediaConfig();

        if (!config) {
            return;
        }

        if (!oldConfig || config.uniqueId !== oldConfig.uniqueId) {
            this.initPlayerOptions(config);
        }

        const player = this.getPlayer(config.type);
        // const player = this.load(uniqueId);
        if (player) {
            config.isPlaying = true;
            player.play();
            const setCurrentTime = () => {
                if (!oldConfig || config.uniqueId !== oldConfig.uniqueId) {
                    player.currentTime = config.isPrimary ? config.lastPlayedTime : 0;
                }
                player.removeEventListener('canplay', setCurrentTime);
            };
            if (isIos) {
                player.addEventListener('canplay', setCurrentTime);
            } else {
                setCurrentTime();
            }
        }
    }

    public stopOtherPlayer(nextId: string) {
        const oldConfig = this.getCurrentMediaConfig();
        if (!oldConfig) {
            return;
        }

        if (oldConfig.uniqueId !== nextId) {
            this.pause(oldConfig.uniqueId, true);
            const player = this.getPlayer(oldConfig.type);
            if (player) {
                if (oldConfig.isPrimary) {
                    oldConfig.lastPlayedTime = player.currentTime;
                } else {
                    player.currentTime = 0;
                }
            }
        }
    }

    public pause(uniqueId: string, isInterrupted?: boolean) {
        const config = MediaList[uniqueId];
        const player = this.getPlayer(config.type);
        // const player = this.load(uniqueId);
        if (player) {
            config.lastPlayedTime = player.currentTime;
            if (!isInterrupted) {
                config.isPlaying = false;
            }
            player.pause();
            this.emit('pause' + '-' + uniqueId);
        }
    }

    public resetPrimaryPlayer() {
        /* 找到主播放器，如果它是在被播放过程中暂停的，就恢复播放 */
        for (const uniqueId in MediaList) {
            if (MediaList.hasOwnProperty(uniqueId)) {
                const config = MediaList[uniqueId];
                if (config.isPrimary && config.isPlaying) {
                    this.play(uniqueId);
                }
            }
        }
    }

    public initPlayer(player: HTMLAudioElement | HTMLVideoElement, type: TMediaType) {
        if (type === 'video') {
            player.setAttribute('playsinline', 'true');
            player.setAttribute('webkit-playsinline', 'true');
            player.setAttribute('controlsList', 'nodownload nofullscreen');
            player.setAttribute('x5-video-player-type', 'h5');
            player.setAttribute('x5-video-player-fullscreen', 'true');
            player.setAttribute('x5-video-orientation', 'landscape|portrait');
        }
    }

    public removeAttribute = (player: HTMLAudioElement | HTMLVideoElement, attributeName: string) => {
        player.removeAttribute(attributeName);
    };

    public bindEvent(player: HTMLAudioElement | HTMLVideoElement) {
        for (const event in EMediaEvent) {
            if (EMediaEvent.hasOwnProperty(event)) {
                player.addEventListener(event, (...args: any[]) => {
                    this.emit(event + '-' + this.playingUniqueId, args);
                    if (event === 'ended') {
                        this.pause(this.playingUniqueId);
                        this.resetPrimaryPlayer();
                    }
                });
            }
        }
    }
}

export default PlayerManager.getInstance();
